/* istanbul ignore file */
import React, { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useMutation } from '@apollo/client';

import LoginRegisterModalContext from '@context/Modal/login-register-modal-context';
import UserContext from '@context/User/user-context';
import useAlert, { TYPES } from '@hooks/useAlert/useAlert';

import CREATE_ROOM_MUTATION from '@graphql/Room/mutations/new-room.graphql';

import CreateProjectModalContext, { ICreateProjectModal } from '@context/Modal/create-project-modal-context';
import CreateProjectModal from '@components/shared/modals/CreateProjectModal/CreateProjectModal';

const SESSION_STORAGE_KEY = 'havenly.pendingCreateProject';

const CreateProjectModalProvider:React.FunctionComponent = ({ children }) => {
  const [
    modalState,
    setModalState,
  ] = useState<ICreateProjectModal | null>(null);
  const [busy, setBusy] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectType, setNewProjectType] = useState('');
  const {
    user, isLoading,
  } = useContext(UserContext);
  const { open } = useContext(LoginRegisterModalContext);
  const router = useRouter();

  const [createRoomMutation] = useMutation(CREATE_ROOM_MUTATION);
  const errorAlert = useAlert(TYPES.ERROR);

  useEffect(() => {
    if (modalState && !isLoading && !user) {
      open({
        closable: true,
        onLoginSuccess: () => {},
        onRegisterSuccess: () => {},
        magicLinkRedirectUrl: router.asPath,
        source: 'create-project',
      });
    }
  }, [user, isLoading, modalState]);

  useEffect(() => {
    const pendingAddToSession = window.sessionStorage
      .getItem(`${SESSION_STORAGE_KEY} ${window.location.href}`);

    if (pendingAddToSession) {
      window.sessionStorage.removeItem(`${SESSION_STORAGE_KEY} ${window.location.href}`);
      setModalState(JSON.parse(pendingAddToSession));
    }
  }, []);

  const closeModal = () => {
    setBusy(false);
    setNewProjectName('');
    setNewProjectType('');
    setModalState(null);
  };

  const createRoom = async () => {
    setBusy(true);

    let result;
    try {
      result = await createRoomMutation({
        variables: {
          name: newProjectName,
          roomTypeId: newProjectType,
        },
      });
    } catch (err) {
      errorAlert(`Failed to create ${newProjectName}.`, 3000);
      setBusy(false);
      return;
    }

    if (result.data && result.data.createRoom) {
      const url = `/rooms/${result.data.createRoom.id}/select-package`;
      window.location.href = url;
    }
  };

  return (
    <CreateProjectModalContext.Provider
      value={{
        open: setModalState,
      }}
    >
      {modalState && user && (
        <CreateProjectModal
          busy={busy}
          newProjectName={newProjectName}
          newProjectType={newProjectType}
          onClose={closeModal}
          onCreate={createRoom}
          setNewProjectName={setNewProjectName}
          setNewProjectType={setNewProjectType}
          presetRoomTypeId={modalState.presetRoomTypeId ? modalState.presetRoomTypeId : undefined}
        />
      )}
      {children}
    </CreateProjectModalContext.Provider>
  );
};

export default CreateProjectModalProvider;
